/**
 * Deep linking with lens state synchronization
 */

import { useState, useEffect, useCallback } from 'react';
import type { AppState } from '../types';

// Default year to load when no URL parameter is specified
const DEFAULT_YEAR = '2025';

const DEFAULT_STATE: AppState = {
  detentId: '',
  lensId: 'full',
  position: 0,
};

export const useUrlSync = () => {
  const [state, setState] = useState<AppState>(DEFAULT_STATE);

  // Parse URL parameters to AppState
  const parseUrlToState = useCallback((): AppState => {
    const params = new URLSearchParams(window.location.search);

    const knownKeys = new Set(['lens', 'position', 'n', 'd', 'detent', 'year']);
    const namedDetent = params.get('d') || params.get('detent') || params.get('year');

    let detentId = namedDetent || '';

    if (!detentId) {
      for (const key of params.keys()) {
        if (!key || knownKeys.has(key)) continue;
        detentId = key;
        break;
      }
    }

    if (!detentId) {
      detentId = DEFAULT_YEAR;
    }

    const lensId = params.get('lens') || DEFAULT_STATE.lensId;
    const positionParam = params.get('position') || params.get('n');
    const parsedPosition = positionParam ? parseFloat(positionParam) : DEFAULT_STATE.position;
    const position = Number.isFinite(parsedPosition) ? parsedPosition : DEFAULT_STATE.position;

    return {
      detentId,
      lensId,
      position,
    };
  }, []);

  // Convert AppState to URL parameters
  const stateToUrl = useCallback((newState: AppState): string => {
    const params = new URLSearchParams();

    if (newState.detentId) {
      params.set('d', newState.detentId);
    }

    if (newState.lensId !== DEFAULT_STATE.lensId) {
      params.set('lens', newState.lensId);
    }

    if (newState.position !== DEFAULT_STATE.position) {
      params.set('position', newState.position.toString());
    }

    return params.toString();
  }, []);

  // Update URL when state changes
  const updateUrl = useCallback((newState: AppState, replace = false) => {
    const queryString = stateToUrl(newState);
    const newUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;

    if (replace) {
      window.history.replaceState(null, '', newUrl);
    } else {
      window.history.pushState(null, '', newUrl);
    }
  }, [stateToUrl]);

  // Update state and URL
  const updateState = useCallback((newState: Partial<AppState>, replace = false) => {
    const updatedState = { ...state, ...newState };
    setState(updatedState);
    updateUrl(updatedState, replace);
  }, [state, updateUrl]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const urlState = parseUrlToState();
      setState(urlState);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [parseUrlToState]);

  // Initialize state from URL on mount
  useEffect(() => {
    const urlState = parseUrlToState();
    setState(urlState);
  }, [parseUrlToState]);

  return {
    state,
    updateState,
    updateDetent: (detentId: string, replace = false) =>
      updateState({ detentId }, replace),
    updateLens: (lensId: string, replace = false) =>
      updateState({ lensId }, replace),
    updatePosition: (position: number, replace = true) =>
      updateState({ position }, replace),
  };
};
